import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Search, DollarSign, Heart, Phone, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AGENT } from "@/data";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Buy", to: "/buy" },
  { label: "Sell", to: "/sell" },
  { label: "Explore Areas", to: "/areas" },
  { label: "Resources", to: "/resources" },
  { label: "Saved Homes", to: "/saved" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent"}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex flex-col leading-none" data-testid="logo-link">
          <span className="font-serif text-xl md:text-2xl text-navy tracking-tight">Georgia Home Connect</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-green">Metro Atlanta Real Estate</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-charcoal hover:text-gold transition-colors">
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="text-sm font-medium text-navy hover:text-gold flex items-center gap-1">
                  <LayoutDashboard size={16} /> Dashboard
                </Link>
              )}
              <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="text-sm font-medium text-charcoal hover:text-gold flex items-center gap-1">
                <LogOut size={16} /> Logout
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-charcoal hover:text-gold">Sign in</Link>
          )}
          <Link to="/contact" data-testid="nav-consult-cta" className="bg-gold text-navy font-semibold text-sm px-6 py-3 rounded-full hover:scale-105 transition-transform">
            Talk With Richard
          </Link>
        </div>
        <button className="lg:hidden text-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-cream border-t border-black/5 px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="text-lg font-medium text-charcoal">{n.label}</Link>
          ))}
          {user?.role === "admin" && <Link to="/admin" className="text-lg font-medium text-navy">Dashboard</Link>}
          {user ? (
            <button onClick={() => { logout(); navigate("/"); }} className="text-left text-lg font-medium text-charcoal">Logout</button>
          ) : (
            <Link to="/login" className="text-lg font-medium text-charcoal">Sign in</Link>
          )}
        </div>
      )}
    </header>
  );
}

const MOBILE_NAV = [
  { label: "Home", to: "/", icon: Home },
  { label: "Search", to: "/search", icon: Search },
  { label: "Sell", to: "/sell", icon: DollarSign },
  { label: "Saved", to: "/saved", icon: Heart },
  { label: "Contact", to: "/contact", icon: Phone },
];

export function MobileNav() {
  const loc = useLocation();
  return (
    <>
      <a href={AGENT.phoneHref} data-testid="mobile-talk-fab"
        className="lg:hidden fixed bottom-24 right-4 z-50 bg-gold text-navy font-semibold text-sm px-5 py-3 rounded-full shadow-2xl flex items-center gap-2">
        <Phone size={16} /> Talk With Richard
      </a>
      <nav className="lg:hidden fixed bottom-4 left-1/2 -translate-x-1/2 w-[92%] max-w-md bg-navy text-white rounded-full px-2 py-2 flex justify-between items-center shadow-2xl z-50 border border-white/10" data-testid="mobile-bottom-nav">
        {MOBILE_NAV.map((n) => {
          const active = loc.pathname === n.to;
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} data-testid={`bottomnav-${n.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-full transition-colors ${active ? "text-gold" : "text-white/70"}`}>
              <Icon size={20} />
              <span className="text-[10px]">{n.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
}

export function Footer() {
  return (
    <footer className="bg-navy text-cream/80 pt-20 pb-32 lg:pb-16 px-6 md:px-12" data-testid="footer">
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="font-serif text-2xl text-white mb-3">Georgia Home Connect</div>
          <p className="text-sm max-w-sm leading-relaxed">Helping you make the right move in Metro Atlanta — Fulton, Gwinnett, DeKalb, and Walton Counties.</p>
          <div className="mt-6 text-sm space-y-1 font-mono">
            <div>{AGENT.name} — {AGENT.title}</div>
            <div>{AGENT.brokerage}</div>
            <div>License #{AGENT.license}</div>
            <div><a href={AGENT.phoneHref} className="hover:text-gold">{AGENT.phone}</a></div>
            <div><a href={`mailto:${AGENT.email}`} className="hover:text-gold break-all">{AGENT.email}</a></div>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 border border-white/20 rounded-lg px-3 py-2 text-xs">
            <span className="font-bold text-white">EHO</span> Equal Housing Opportunity
          </div>
        </div>
        <div>
          <div className="text-white font-medium mb-4">Explore</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/buy" className="hover:text-gold">Buy a Home</Link></li>
            <li><Link to="/sell" className="hover:text-gold">Sell a Home</Link></li>
            <li><Link to="/areas" className="hover:text-gold">Explore Areas</Link></li>
            <li><Link to="/search" className="hover:text-gold">Property Search</Link></li>
            <li><Link to="/calculators" className="hover:text-gold">Calculators</Link></li>
            <li><Link to="/resources" className="hover:text-gold">Resources</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-medium mb-4">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="hover:text-gold">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-gold">Terms of Use</Link></li>
            <li><Link to="/accessibility" className="hover:text-gold">Accessibility</Link></li>
            <li><Link to="/contact" className="hover:text-gold">Contact Richard</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-14 pt-8 border-t border-white/10 text-xs text-cream/50 space-y-2">
        <p><strong className="text-cream/70">Placeholder content notice:</strong> Property listings shown are sample/placeholder data for demonstration and are not live MLS or IDX data. No third-party listing data is scraped or represented as live.</p>
        <p><strong className="text-cream/70">Estimates disclaimer:</strong> Mortgage, affordability, and home-value figures are educational estimates only — not offers, approvals, or guarantees. An agent-prepared market analysis is required for a complete home-value estimate.</p>
        <p>&copy; {new Date().getFullYear()} {AGENT.name}, {AGENT.brokerage}. All rights reserved.</p>
      </div>
    </footer>
  );
}
